import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Session, SessionOperator, SessionTenant, SessionUser } from "@/types/api";

/**
 * Estado de sessao do cliente.
 *
 * O que este store NAO guarda: token. Os tokens vivem exclusivamente em
 * cookie httpOnly; o frontend nem consegue le-los (CLAUDE.md 6). O que fica
 * aqui e so a identidade ja resolvida, para o router decidir rota e o layout
 * mostrar o nome sem esperar um round-trip.
 *
 * Persistido porque um refresh de pagina nao pode piscar a tela de login
 * quando o cookie continua valido. A verdade continua sendo o servidor: o
 * `GET /me` do bootstrap confirma ou limpa este estado. Guardar identidade
 * aqui e conveniencia de renderizacao, nunca autorizacao.
 */
interface SessionState {
  user: SessionUser | null;
  operator: SessionOperator | null;
  tenant: SessionTenant | null;
  /** Ainda nao sabemos se ha sessao — evita piscar login antes do /me. */
  status: "unknown" | "authenticated" | "anonymous";

  setSession: (session: Session) => void;
  clearSession: () => void;
  markAnonymous: () => void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      user: null,
      operator: null,
      tenant: null,
      status: "unknown",

      setSession: (session) =>
        set({
          user: session.user ?? null,
          operator: session.operator ?? null,
          tenant: session.tenant ?? null,
          status: session.user || session.operator ? "authenticated" : "anonymous",
        }),

      clearSession: () => set({ user: null, operator: null, tenant: null, status: "anonymous" }),

      markAnonymous: () => set({ status: "anonymous" }),
    }),
    {
      name: "conecta.session",
      storage: createJSONStorage(() => localStorage),
      // `status` fica de fora de proposito: ao recarregar a pagina a sessao
      // volta a ser "unknown" e o bootstrap reconfirma no servidor.
      // Persistir "authenticated" faria a UI confiar num estado que o
      // servidor talvez ja tenha invalidado.
      partialize: (state) => ({ user: state.user, operator: state.operator, tenant: state.tenant }),
    },
  ),
);

export const selectCurrentActor = (state: SessionState) => state.user ?? state.operator;
export const selectRole = (state: SessionState) =>
  state.operator ? "operator" : (state.user?.role ?? null);
